import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import StatCard from '../components/StatCard';
import TransactionCard from '../components/TransactionCard';
import QuickAddButton from '../components/QuickAddButton';
import TransactionModal from '../components/TransactionModal';
import { Calendar, TrendingUp } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

const Dashboard = () => {
    const [stats, setStats] = useState({
        totalIncome: 0,
        totalExpense: 0,
        netBalance: 0
    });
    const [ledgers, setLedgers] = useState([]);
    const [selectedLedger, setSelectedLedger] = useState('');
    const [recentTransactions, setRecentTransactions] = useState([]);
    const [categoryData, setCategoryData] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState('expense');

    const fetchDashboardData = async () => {
        try {
            // Fetch ledgers
            const ledgersResponse = await api.get('/ledgers');
            // Backend returns {success, count, data}
            const fetchedLedgers = ledgersResponse.data.data || ledgersResponse.data;
            setLedgers(Array.isArray(fetchedLedgers) ? fetchedLedgers.map(l => l.name) : []);

            if (fetchedLedgers.length > 0 && !selectedLedger) {
                setSelectedLedger(fetchedLedgers[0].name);
            }

            // Calculate overall stats
            const totalIncome = fetchedLedgers.reduce((acc, curr) => acc + (curr.totalIncome || 0), 0);
            const totalExpense = fetchedLedgers.reduce((acc, curr) => acc + (curr.totalExpense || 0), 0);
            const netBalance = totalIncome - totalExpense;

            setStats({
                totalIncome,
                totalExpense,
                netBalance
            });

            // Fetch transactions for the selected ledger (or first one) to show recent transactions
            // Note: Ideally we should have an endpoint for "recent transactions across all ledgers"
            // For now, we'll use the first ledger's transactions if available
            if (fetchedLedgers.length > 0) {
                const targetLedgerId = fetchedLedgers[0]._id; // Default to first for now
                const transactionsResponse = await api.get(`/transactions/ledger/${targetLedgerId}`);
                // Backend returns {success, count, data}
                const transactionsData = transactionsResponse.data.data || transactionsResponse.data;
                setRecentTransactions(Array.isArray(transactionsData) ? transactionsData.slice(0, 5) : []); // Show top 5

                // Calculate category data for the chart from these transactions
                // This is a placeholder logic. Real logic should aggregate across all ledgers or use a specific endpoint.
                const categories = {};
                if (Array.isArray(transactionsData)) {
                    transactionsData.forEach(t => {
                        if (t.type === 'expense') {
                            categories[t.category] = (categories[t.category] || 0) + t.amount;
                        }
                    });
                }

                const chartData = Object.keys(categories).map((cat, index) => ({
                    name: cat,
                    value: categories[cat],
                    color: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'][index % 5]
                }));
                setCategoryData(chartData);
            }

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, [selectedLedger]); // Re-fetch if selectedLedger changes (if we implement specific ledger filtering)

    const handleAddIncome = () => {
        setModalType('income');
        setIsModalOpen(true);
    };

    const handleAddExpense = () => {
        setModalType('expense');
        setIsModalOpen(true);
    };

    const handleTransactionSuccess = () => {
        setIsModalOpen(false);
        fetchDashboardData();
    };

    return (
        <div className="p-6 space-y-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
                    <p className="text-gray-500 mt-0.5 text-sm">Welcome back! Here's your financial overview.</p>
                </div>
                <div className="flex items-center gap-3">
                    <select
                        value={selectedLedger}
                        onChange={(e) => setSelectedLedger(e.target.value)}
                        className="px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                        {ledgers.map((ledger) => (
                            <option key={ledger} value={ledger}>{ledger}</option>
                        ))}
                    </select>
                    <div className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 text-sm">
                        <Calendar size={16} />
                        <span className="font-medium">December 2025</span>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard
                    title="Total Income"
                    amount={stats.totalIncome}
                    type="income"
                    trend={12.5}
                />
                <StatCard
                    title="Total Expenses"
                    amount={stats.totalExpense}
                    type="expense"
                    trend={-8.3}
                />
                <StatCard
                    title="Net Balance"
                    amount={stats.netBalance}
                    type="balance"
                    trend={25.8}
                />
            </div>

            {/* Charts and Transactions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Category Breakdown */}
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-base font-bold text-gray-900">Expense Categories</h2>
                        <TrendingUp size={18} className="text-indigo-600" />
                    </div>
                    <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                            <Pie
                                data={categoryData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                outerRadius={90}
                                fill="#8884d8"
                                dataKey="value"
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                                {categoryData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => `₹${value.toLocaleString('en-IN')}`} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Recent Transactions */}
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-base font-bold text-gray-900">Recent Transactions</h2>
                        <button className="text-indigo-600 text-xs font-medium hover:text-indigo-700">
                            View All
                        </button>
                    </div>
                    <div className="space-y-2.5 max-h-[280px] overflow-y-auto">
                        {recentTransactions.map((transaction) => (
                            <TransactionCard key={transaction._id} transaction={transaction} />
                        ))}
                    </div>
                </div>
            </div>

            {/* Quick Add Button */}
            <QuickAddButton onAddIncome={handleAddIncome} onAddExpense={handleAddExpense} />

            {/* Transaction Modal */}
            <TransactionModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                type={modalType}
                onSuccess={handleTransactionSuccess}
            />
        </div>
    );
};

export default Dashboard;
